import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { useRoute } from 'vue-router'

import { useAppStore } from '@/stores/app'

/**
 * Layout state shared by `Default.vue` and its components (vue-spec `structure.md` —
 * `layouts/layout.ts`). Module-level, because there is one chrome per window: two copies of
 * "is the sidebar open" is how a menu closes itself on one component and not the other.
 *
 * Dark mode is deliberately NOT here, and it is not the topbar's either. Nuxt UI auto-registers
 * VueUse's `useDark`, and `stores/app.ts` wraps that SAME state as `colorMode` — one source of
 * truth, per vue-spec `theme.md`. The topbar only renders `ColorModeToggle`; duplicating the
 * state here would be the second source of truth theme.md forbids.
 */
/**
 * THE ADMINISTRATION MENU, in one place. `Sidebar.vue` renders it and `Topbar.vue` reads the
 * CURRENT entry's label for the page title the Nuxt UI dashboard navbar shows at the left —
 * two components, one list, so a section can never be named one thing in the menu and another
 * in the title bar. It lists only routes that EXIST.
 *
 * `routes` is every route of a section, so a DETAIL page (a user, a client) keeps its section
 * highlighted in the menu and titled in the navbar. The labels are i18n KEYS, not prose: the
 * strings stay in `libs/i18n/locales/*`.
 */
export interface NavItem {
    /** The route this entry navigates to. */
    name:     string
    /** The i18n key of its label (`nav.*`). */
    labelKey: string
    icon:     string
    /** Every route of this section. */
    routes:   string[]
}

/**
 * A SECTION of the menu. `labelKey` is the small uppercase header above the group, or `null`
 * for the opening group — the landing page needs no heading, and one on its own would only
 * push the first row away from the brand.
 */
export interface NavGroup {
    labelKey: string | null
    items:    NavItem[]
}

/**
 * THE MENU, IN GROUPS. Rows in one undifferentiated column say nothing about what the app is
 * FOR; grouped under terse headers they say it in a word or two. List only routes that EXIST.
 *
 * Replace the example below with the project's own sections, e.g.
 *
 *     {
 *         labelKey: 'nav.group_management',
 *         items: [
 *             { name: 'orders', labelKey: 'nav.orders', icon: 'i-lucide-package', routes: ['orders', 'order'] },
 *         ],
 *     },
 */
export const NAV_GROUPS: NavGroup[] = [
    {
        labelKey: 'nav.group_overviews',
        items: [
            { name: 'pregled',            labelKey: 'nav.overview',           icon: 'i-lucide-layout-dashboard', routes: ['pregled'] },
            { name: 'projekti',            labelKey: 'nav.projects',           icon: 'i-lucide-folder-kanban',    routes: ['projekti'] },
            { name: 'kakovost',            labelKey: 'nav.data_quality',       icon: 'i-lucide-shield-check',     routes: ['kakovost'] },
            { name: 'prihodki-stroski',    labelKey: 'nav.revenue_costs',      icon: 'i-lucide-chart-column',     routes: ['prihodki-stroski'] },
            { name: 'denarni-tok',         labelKey: 'nav.cash_flow',          icon: 'i-lucide-arrow-left-right', routes: ['denarni-tok'] },
            { name: 'placilna-disciplina', labelKey: 'nav.payment_discipline', icon: 'i-lucide-clock',            routes: ['placilna-disciplina'] },
            { name: 'uvozi',               labelKey: 'nav.imports',            icon: 'i-lucide-upload',           routes: ['uvozi'] },
        ],
    },
    {
        labelKey: 'nav.group_bmapp',
        items: [
            { name: 'napredek-projektov', labelKey: 'nav.project_progress', icon: 'i-lucide-gauge',      routes: ['napredek-projektov'] },
            { name: 'ekipe',              labelKey: 'nav.teams',            icon: 'i-lucide-users',      routes: ['ekipe'] },
            { name: 'prodaja',            labelKey: 'nav.sales',            icon: 'i-lucide-trending-up', routes: ['prodaja'] },
        ],
    },
    {
        labelKey: 'nav.group_projects',
        items: [
            { name: 'marza',    labelKey: 'nav.margin', icon: 'i-lucide-percent', routes: ['marza'] },
            { name: 'povezave', labelKey: 'nav.links',  icon: 'i-lucide-link',    routes: ['povezave'] },
        ],
    },
]

/** Every entry, flattened — what the topbar titles the page from. The groups are the model. */
export const NAV_ITEMS: NavItem[] = NAV_GROUPS.flatMap((group) => group.items)

/** The i18n key of the page title for a route, falling back to the product name. */
export const navTitleKey = (routeName: string): string =>
    NAV_ITEMS.find((item) => item.routes.includes(routeName))?.labelKey ?? 'brand.product_name'

/**
 * IS THE MOBILE DRAWER OPEN. Module-level, and the only copy — below 1024px the sidebar is not
 * a column any more but an overlay over the whole shell, and an overlay that two components
 * disagree about is an overlay that cannot be closed.
 *
 * It only ever MEANS anything on mobile. On the desktop the sidebar is rendered because it is
 * the layout's left column, not because this is `true`, which is why `useLayoutShell()` forces
 * it back to `false` the moment the window grows past the breakpoint: a flag left set by a
 * phone-width session would otherwise re-open the drawer the next time the window narrows.
 */
const menuOpen = ref(false)

const openMenu = (): void => {
    menuOpen.value = true
}

const closeMenu = (): void => {
    menuOpen.value = false
}

const toggleMenu = (): void => {
    menuOpen.value = !menuOpen.value
}

/**
 * The state and the ONE action per verb. Every component that can open or close the drawer —
 * the topbar's hamburger, the drawer's own X, a nav row, the backdrop — calls one of these
 * three; none of them writes `menuOpen` itself.
 */
export const useLayout = () => {
    const appStore = useAppStore()

    const isMobile = computed((): boolean => appStore.isMobile())

    return { menuOpen, isMobile, openMenu, closeMenu, toggleMenu }
}

/**
 * THE DRAWER'S BEHAVIOUR, wired once by `Default.vue` and nowhere else.
 *
 * A drawer that can be opened and not closed is what the shell shipped with, so the four ways
 * out live together here rather than one per component: Escape, a route change, growing past
 * the breakpoint, and — in the layout, because it is a paint and not a behaviour — the
 * backdrop. While it is open the body cannot scroll, or the page behind the overlay scrolls
 * under the thumb instead of the menu.
 *
 * The listener is on `document` for the whole life of the shell rather than added and removed
 * around `menuOpen`: one registration, no window where a key lands between the watcher and the
 * listener. It does nothing at all unless the drawer is open.
 */
export const useLayoutShell = () => {
    const { isMobile } = useLayout()
    const route        = useRoute()

    /** What `body` had before the drawer took it — restored verbatim, not blanked. */
    let bodyOverflow: string | null = null

    const lockBody = (): void => {
        if (bodyOverflow === null) {
            bodyOverflow = document.body.style.overflow
        }

        document.body.style.overflow = 'hidden'
    }

    const unlockBody = (): void => {
        if (bodyOverflow === null) {
            return
        }

        document.body.style.overflow = bodyOverflow
        bodyOverflow = null
    }

    const onKeydown = (event: KeyboardEvent): void => {
        if (event.key === 'Escape' && menuOpen.value) {
            closeMenu()
        }
    }

    // Growing past the breakpoint: the sidebar is the layout's own column again, so the flag
    // that made it an overlay has to go with it.
    watch(isMobile, (mobile): void => {
        if (!mobile) {
            closeMenu()
        }
    })

    // Navigating is the most common way out of the menu — the row that was tapped has already
    // done its job. `Sidebar.vue` closes on select too; this catches every other route change
    // (a redirect, the back button, a link inside the page).
    watch(() => route.path, (): void => {
        closeMenu()
    })

    watch([menuOpen, isMobile], ([open, mobile]): void => {
        if (open && mobile) {
            lockBody()
        } else {
            unlockBody()
        }
    })

    onMounted((): void => {
        document.addEventListener('keydown', onKeydown)
    })

    onBeforeUnmount((): void => {
        document.removeEventListener('keydown', onKeydown)
        unlockBody()
    })
}
